import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

type MessageHandler = (msg: IMessage) => void;
export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error' | 'reconnecting';
type StatusListener = (status: ConnectionStatus) => void;

class WebSocketManager {
    private client: Client | null = null;
    private subscriptions = new Map<string, string>(); // destination → subscriptionId
    private handlers = new Map<string, MessageHandler>();
    private statusListeners = new Set<StatusListener>();

    private disconnectTimer: ReturnType<typeof setTimeout> | null = null;

    connect() {
        if (this.client && this.client.connected) return;

        const url = `${process.env.REACT_APP_API_BASE_URL}/ws`;

        return new Promise<void>((resolve, reject) => {
            this.notifyStatus('connecting');

            this.client = new Client({
                webSocketFactory: () => new SockJS(url, undefined, {
                    withCredentials: true
                }),
                reconnectDelay: 5000,
                debug: (str) => console.debug('[STOMP]', str),
            });

            this.client.onConnect = () => {
                console.debug('WebSocket connected');

                if (this.disconnectTimer) {
                    clearTimeout(this.disconnectTimer);
                    this.disconnectTimer = null;
                }

                this.notifyStatus('connected');

                this.handlers.forEach((handler, destination) => {
                    const sub = this.client!.subscribe(destination, handler);
                    this.subscriptions.set(destination, sub.id);
                });

                resolve();
            };

            this.client.onWebSocketClose = () => {
                console.debug('WebSocket closed, will attempt reconnect');

                this.notifyStatus('reconnecting');

                // 기존 타이머 있으면 제거
                if (this.disconnectTimer) {
                    clearTimeout(this.disconnectTimer);
                }

                // 👇 유예 시간 후에만 disconnected
                this.disconnectTimer = setTimeout(() => {
                    console.debug('WebSocket disconnected (grace timeout)');
                    this.notifyStatus('disconnected');
                    this.disconnectTimer = null;
                }, 10000);
            };

            this.client.onStompError = (frame) => {
                console.error('Broker error:', frame.headers['message']);
                this.notifyStatus('error');
                reject(new Error('STOMP error'));
            };

            this.client.onWebSocketError = (event) => {
                console.error('WebSocket error', event);
                this.notifyStatus('error');
                reject(new Error('WebSocket connection error'));
            };

            this.client.onDisconnect = () => {
                console.debug('WebSocket disconnected');
                this.notifyStatus('disconnected');
            };

            this.client.activate();
        });
    }

    disconnect() {
        this.client?.deactivate();
        this.client = null;
        this.subscriptions.clear();
        this.handlers.clear();
        this.notifyStatus('disconnected');
    }

    subscribe(destination: string, handler: MessageHandler) {
        this.handlers.set(destination, handler);
        if (this.client && this.client.connected) {
            // Idempotent: drop any existing subscription for this destination first. On reconnect,
            // onConnect re-subscribes every handler and the caller's effect may subscribe again — without
            // this, the same destination ends up with two live subscriptions and the handler fires twice.
            const existing = this.subscriptions.get(destination);
            if (existing) {
                this.client.unsubscribe(existing);
            }
            const sub = this.client.subscribe(destination, handler);
            this.subscriptions.set(destination, sub.id);
        }
    }

    unsubscribe(destination: string) {
        if (!this.client || !this.client.connected) {
            console.warn(`[WS] Cannot unsubscribe "${destination}" — client not connected.`);
            return;
        }

        const subId = this.subscriptions.get(destination);
        if (subId) {
            this.client.unsubscribe(subId);
            this.subscriptions.delete(destination);
            this.handlers.delete(destination);
        }
    }

    send(destination: string, body: any) {
        if (!this.client || !this.client.connected) {
            console.error('WebSocket not connected');
            return;
        }
        this.client.publish({
            destination,
            body: JSON.stringify(body),
        });
    }

    addStatusListener(listener: StatusListener) {
        this.statusListeners.add(listener);
    }

    removeStatusListener(listener: StatusListener) {
        this.statusListeners.delete(listener);
    }

    private notifyStatus(status: ConnectionStatus) {
        this.statusListeners.forEach((listener) => listener(status));
    }
}

export default new WebSocketManager();